import React, {
  createContext,
  FC,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { useQuery } from "@app/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import { NotificationTemplateName } from "@app/hooks/useGeneratedTokenizedLink";

export interface PreBillContextProps {
  orderUidPublic: string | null;
  patientUid: string | null;
  notificationType: NotificationTemplateName | null;
  setOrderUidPublic: (orderUidPublic: string) => void;
  setPatientUid: (patientUid: string) => void;
  setNotificationType: (notificationType: NotificationTemplateName) => void;
}

export const PreBillContext = createContext<PreBillContextProps>({
  orderUidPublic: null,
  patientUid: null,
  notificationType: null,
  setOrderUidPublic: () => undefined,
  setPatientUid: () => undefined,
  setNotificationType: () => undefined,
});

interface PreBillProviderProps {
  children: ReactNode;
}

const PreBillProvider: FC<PreBillProviderProps> = ({ children }) => {
  const [orderUidPublic, setOrderUidPublic] = useState<string | null>(null);
  const [patientUid, setPatientUid] = useState<string | null>(null);
  const [notificationType, setNotificationType] =
    useState<NotificationTemplateName | null>(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const query = useQuery();
  const token = query.get("token");

  const allowedPreBillRoutes = useMemo(
    () => [
      routes.billingEstimate,
      routes.nonCoveredService,
      routes.outOfNetwork,
      routes.priorAuthorization,
      routes.noLongerAvailable,
      routes.insuranceNoLongerAvailable,
    ],
    []
  );

  useEffect(() => {
    const isComingFromAllowedRoute = allowedPreBillRoutes.includes(pathname);

    if (isComingFromAllowedRoute && !orderUidPublic && token) {
      navigate(`${routes.guestLogin}?token=${token}`);
    }
  }, [orderUidPublic, token, allowedPreBillRoutes, pathname]);

  return (
    <PreBillContext.Provider
      value={{
        orderUidPublic,
        setOrderUidPublic,
        patientUid,
        setPatientUid,
        notificationType,
        setNotificationType,
      }}
    >
      {children}
    </PreBillContext.Provider>
  );
};

export default PreBillProvider;
